import { IonContent, IonPage } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useHistory } from "react-router";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";

const CONTACT_FORM_URL = process.env.REACT_APP_CONTACT_FORM_URL
    || "https://us-central1-intense-signer-264404.cloudfunctions.net/contactForm";

const JOB_AREAS = [
    "Engineering",
    "Marketing",
    "Design",
    "Operations",
    "Content & Social Media",
    "Community",
    "Other",
];

const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const inputClass = "w-full bg-white/[0.06] border border-white/[0.12] rounded-lg px-4 py-3 text-white text-[15px] font-['Mukta_Mahee'] placeholder:text-white/30 focus:outline-none focus:border-highlight/60 focus:ring-1 focus:ring-highlight/30 transition-colors duration-200";
const labelClass = "block text-[13px] font-bold text-white/80 tracking-[0.3px] mb-2";

type Status = "idle" | "submitting" | "success" | "error";

const CareersPage: React.FC = () => {
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { trackScreen } = useAnalytics();

    useEffect(() => {
        trackScreen(Screens.CAREERS);
    }, [trackScreen]);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [jobArea, setJobArea] = useState("");
    const [linkedinUrl, setLinkedinUrl] = useState("");
    const [message, setMessage] = useState("");
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState("");
    const [website, setWebsite] = useState(""); // honeypot

    const [status, setStatus] = useState<Status>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleBack = () => {
        if (history.length > 1) history.goBack();
        else history.push("/");
    };

    const isValid = () =>
        name.trim().length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        jobArea.length > 0;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileError("");
        const file = e.target.files?.[0];
        if (!file) {
            setResumeFile(null);
            return;
        }

        const hasValidMime = ALLOWED_MIME_TYPES.includes(file.type);
        const hasValidExt = ALLOWED_EXTENSIONS.some((ext) =>
            file.name.toLowerCase().endsWith(ext)
        );
        if (!hasValidMime && !hasValidExt) {
            setFileError("Please upload a PDF or Word document (.pdf, .doc, .docx).");
            setResumeFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setFileError("File exceeds 10MB limit.");
            setResumeFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setResumeFile(file);
    };

    const handleRemoveFile = () => {
        setResumeFile(null);
        setFileError("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (website) return; // honeypot silently
        if (!isValid()) {
            setErrorMessage("Please fill in all required fields with valid information.");
            setStatus("error");
            return;
        }
        if (fileError) {
            setErrorMessage("Please fix the file upload error before submitting.");
            setStatus("error");
            return;
        }

        setStatus("submitting");
        setErrorMessage("");

        try {
            const formData = new FormData();
            formData.append("formType", "careers");
            formData.append("name", name.trim());
            formData.append("email", email.trim());
            formData.append("jobArea", jobArea);
            formData.append("linkedinUrl", linkedinUrl.trim());
            formData.append("message", message.trim());
            formData.append("website", website);
            if (resumeFile) {
                formData.append("resume", resumeFile, resumeFile.name);
            }

            // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.
            const response = await fetch(CONTACT_FORM_URL, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({ error: "Submission failed" }));
                if (response.status === 429) {
                    throw new Error("You've submitted too many requests. Please try again in an hour.");
                }
                throw new Error(data.error || "Something went wrong. Please try again.");
            }

            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
        }
    };

    return (
        <IonPage>
            <CustomHelmet
                title="Careers — Ecliptic"
                description="Join the team building Ecliptic, the app for park skiers and snowboarders serious about progression."
                url="https://ecliptic.day/careers"
            />
            <IonContent>
                <div className="min-h-full bg-black text-white font-['Mukta_Mahee']">
                    <nav className="sticky top-0 z-50 bg-[rgba(0,0,0,0.75)] backdrop-blur-[24px] border-b border-[rgba(88,97,172,0.18)]">
                        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-white/65 hover:text-white text-sm font-bold tracking-[0.4px] transition-colors duration-200"
                                aria-label="Go back"
                            >
                                ← Back
                            </button>
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[26px] w-auto object-contain block"
                            />
                        </div>
                    </nav>

                    <article className="max-w-2xl mx-auto px-6 py-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
                            Careers
                        </h1>
                        <p className="text-white/65 text-[15px] leading-[1.6] mb-10">
                            We're a small collective of freeskiers, snowboarders, engineers, and
                            builders making something we wished existed. If you want to help build
                            the home for action sports — tell us a bit about yourself.
                        </p>

                        {status === "success" ? (
                            <div className="bg-highlight/10 border border-highlight/30 rounded-lg p-6">
                                <h2 className="text-xl font-bold mb-2">Thanks — we'll be in touch.</h2>
                                <p className="text-white/70 text-[15px]">
                                    Mia will follow up personally. In the meantime, feel free to explore the rest of the site.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => history.push("/")}
                                    className="mt-4 px-6 py-2 bg-highlight text-white font-bold text-[14px] rounded-lg hover:bg-highlight/80 transition-colors"
                                >
                                    Back to Home
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                                <div>
                                    <label htmlFor="careers-name" className={labelClass}>
                                        Full Name <span className="text-highlight-danger">*</span>
                                    </label>
                                    <input
                                        id="careers-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className={inputClass}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="careers-email" className={labelClass}>
                                        Email <span className="text-highlight-danger">*</span>
                                    </label>
                                    <input
                                        id="careers-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={inputClass}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="careers-area" className={labelClass}>
                                        Job Area <span className="text-highlight-danger">*</span>
                                    </label>
                                    <select
                                        id="careers-area"
                                        value={jobArea}
                                        onChange={(e) => setJobArea(e.target.value)}
                                        className={`${inputClass} appearance-none cursor-pointer`}
                                        required
                                        aria-required="true"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23ffffff80' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 16px center",
                                            paddingRight: "40px",
                                        }}
                                    >
                                        <option value="" disabled>Select one...</option>
                                        {JOB_AREAS.map((area) => (
                                            <option key={area} value={area} className="bg-black text-white">{area}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="careers-linkedin" className={labelClass}>
                                        LinkedIn / Portfolio URL
                                    </label>
                                    <input
                                        id="careers-linkedin"
                                        type="url"
                                        value={linkedinUrl}
                                        onChange={(e) => setLinkedinUrl(e.target.value)}
                                        placeholder="https://..."
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>
                                        Resume (PDF or Word, max 10MB)
                                    </label>
                                    <input
                                        ref={fileInputRef}
                                        id="careers-resume"
                                        type="file"
                                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    {!resumeFile ? (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full sm:w-auto px-6 py-3 bg-white/[0.06] border border-white/[0.12] rounded-lg text-white text-[14px] font-bold hover:bg-white/[0.1] hover:border-white/20 transition-colors duration-200"
                                        >
                                            Choose File
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-3 bg-white/[0.06] border border-white/[0.12] rounded-lg px-4 py-3">
                                            <span className="text-white text-[14px] flex-1 truncate">
                                                {resumeFile.name}
                                            </span>
                                            <span className="text-white/50 text-[12px] shrink-0">
                                                {(resumeFile.size / 1024 / 1024).toFixed(2)}MB
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleRemoveFile}
                                                className="text-white/65 hover:text-highlight-danger text-[13px] font-bold shrink-0"
                                                aria-label="Remove file"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                    {fileError && (
                                        <div className="text-highlight-danger text-[13px] mt-2" role="alert">
                                            {fileError}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="careers-message" className={labelClass}>
                                        Brief Message
                                    </label>
                                    <textarea
                                        id="careers-message"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Tell us a bit about yourself and why you want to build at Ecliptic..."
                                        rows={5}
                                        className={`${inputClass} resize-y min-h-[120px]`}
                                    />
                                </div>

                                {/* Honeypot — hidden from real users */}
                                <input
                                    type="text"
                                    name="website"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    className="hidden"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />

                                {status === "error" && errorMessage && (
                                    <div className="text-highlight-danger text-[14px]" role="alert">
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    className="w-full sm:w-auto px-8 py-3 bg-highlight text-white font-bold text-[15px] rounded-lg tracking-[0.3px] transition-all duration-200 hover:bg-highlight/80 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {status === "submitting" ? "Submitting..." : "Submit"}
                                </button>
                            </form>
                        )}
                    </article>

                    <footer className="max-w-3xl mx-auto px-6 py-10 border-t border-white/10 mt-12">
                        <div className="flex flex-wrap gap-6 text-sm text-white/50">
                            <a href="/invest" className="hover:text-white transition-colors">Invest & Partner</a>
                            <a href="/careers" className="hover:text-white transition-colors">Careers</a>
                            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
                            <a href="/eula" className="hover:text-white transition-colors">EULA</a>
                            <a href="/" className="hover:text-white transition-colors ml-auto">Home</a>
                        </div>
                    </footer>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default CareersPage;
